import React from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { twMerge } from 'tailwind-merge';
import IconComponent from 'components/iconComponent';
import ButtonComponent from 'components/button/buttonComponent';
import { ReactComponent as ErrorIcon } from 'assets/icons/warning.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';

import { ReactComponent as Doc } from 'assets/icons/file.svg';
import { ReactComponent as Close } from 'assets/icons/close.svg';

import {
  FieldValues,
  Path,
  PathValue,
  useController,
  useFormContext,
} from 'react-hook-form';
import FieldLabel from './FieldLabel';
import { useFileQuestionnaireContext } from '../../../app/manage-test-kits/components/MicrobiomeQuestionnaire/QuestionnaireSection';
import { formatFileSize } from 'utilities/fileHelpers';
import { FileOutput } from '../../../graphql/generated/hasura';
import Loader from '../../loaderComponent';

export type ControlledDropzoneProps<T extends FieldValues> = {
  name: Path<T>;
  label?: string | React.ReactNode;
  filesName?: Path<T>;
  labelDropzone?: string;
  placeholder?: string | null;
  btnText?: string;
  disabled?: boolean;
  isLoading?: boolean;
  isRequired?: boolean;
  supportText?: string;
  className?: string;
  children?: React.ReactNode | null;
  disabledAside?: React.ReactNode | boolean;
  labelClassName?: string;
  onUpload?: (files: File[]) => Promise<void>;
  onChange?: (files: File[]) => void;
} & DropzoneOptions;

interface CustomProps {
  label?: string | React.ReactNode;
  id: string;
  supportText?: string;
  btnText?: string;
  className?: string;
  error?: string | null;
  isLoading?: boolean;
  isRequired?: boolean;
  clearErrors: () => void;
  value?: FileList;
  disabledAside?: React.ReactNode | boolean;
}

type ControlledImageDropzoneProps<T extends FieldValues> = {
  name: Path<T>;
  label?: string;
  labelDropzone?: string;
  supportText?: string;
  className?: string;
  disabled?: boolean;
  isRequired?: boolean;
  isUploading?: boolean;
  labelClassName?: string;
  onUpload?: (file: File) => Promise<{ file: FileOutput }>;
} & DropzoneOptions;

interface IDropzone extends DropzoneOptions, CustomProps {}

export const Dropzone: React.FC<IDropzone> = ({
  onDrop,
  supportText,
  accept,
  label,
  className,
  error,
  disabled,
  isLoading,
  disabledAside,
  id,
}) => {
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    noClick: true,
    accept,
  });

  return (
    <div
      className={twMerge(
        'w-full min-h-[240px] p-4 flex flex-col items-center justify-center border border-dashed border-med-gray rounded-lg',
        className,
        error && 'border-[#BF3F3F] bg-[#FFF4C6]',
      )}
      {...getRootProps()}
      id={id}
    >
      <input {...getInputProps()} />
      {isDragActive ? (
        <p>Drop the files here ...</p>
      ) : (
        <>
          <p className="font-semibold text-lg text-neutral-800">{label}</p>
          <p
            className={twMerge(
              'font-semibold mt-1.5 whitespace-pre-wrap text-sm text-med-gray-3 text-center',
            )}
          >
            {supportText}
          </p>

          {error && (
            <div className="text-[#BF3F3F] text-xs font-semibold flex items-start gap-1 mt-2.5">
              <IconComponent
                width={'w-4'}
                height={'h-[16px]'}
                icon={ErrorIcon}
                fill={'fill-current'}
                stroke={'stroke-current'}
              />
              <p className="text-center">{error}</p>
            </div>
          )}
          {disabledAside ? (
            disabledAside
          ) : (
            <ButtonComponent
              className="mt-2.5 w-auto !text-sm !rounded-[5px]"
              onClick={open}
              testID={'dropzone'}
              htmlButtonType="button"
              showSpinner={isLoading}
              disabled={isLoading || disabled}
            >
              Choose File
            </ButtonComponent>
          )}
        </>
      )}
    </div>
  );
};

const FormFileDropzone = <T extends FieldValues>({
  name,
  label,
  btnText,
  supportText,
  isLoading,
  disabled,
  onUpload,
  className,
  isRequired,
  onChange,
  disabledAside,
  ...dropzoneOptions
}: ControlledDropzoneProps<T>) => {
  const { control, clearErrors } = useFormContext<T>();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: undefined,
  });

  return (
    <Dropzone
      id={name}
      label={label}
      supportText={supportText}
      value={field.value}
      error={error?.message}
      clearErrors={() => clearErrors(name)}
      disabled={isLoading || disabled}
      isRequired={isRequired}
      isLoading={isLoading}
      btnText={btnText}
      className={className}
      disabledAside={disabledAside}
      onDrop={async (acceptedFiles, fileRejections) => {
        const filesToValidate =
          acceptedFiles.length > 0
            ? acceptedFiles
            : fileRejections.map((r) => r.file);

        onChange ? onChange(filesToValidate) : field.onChange(filesToValidate);
        if (acceptedFiles.length > 0 && onUpload) {
          try {
            await onUpload(acceptedFiles);
          } catch (e) {
            console.error('Upload failed', e);
          }
        }
      }}
      {...dropzoneOptions}
    />
  );
};

export const FormDropzoneField = <T extends FieldValues>({
  name,
  label,
  labelDropzone,
  btnText,
  supportText,
  className,
  disabled,
  placeholder,
  labelClassName,
  isRequired,
  children,
  ...dropzoneOptions
}: ControlledDropzoneProps<T>) => {
  const { control, clearErrors, setValue } = useFormContext<T>();
  const { onDrop, isUploading } = useFileQuestionnaireContext();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: undefined,
  });

  return (
    <div
      className={twMerge(
        'flex w-full flex-col gap-0.5',
        disabled && 'opacity-50',
      )}
    >
      <FieldLabel
        className={labelClassName}
        name={name}
        label={label}
        isRequired={isRequired}
      />
      {placeholder && (
        <p className="text-med-gray-3 font-medium">{placeholder}</p>
      )}
      {children}
      <Dropzone
        id={name as string}
        label={labelDropzone}
        supportText={supportText}
        value={field.value}
        error={error?.message}
        clearErrors={() => clearErrors(name)}
        disabled={isUploading}
        isRequired={isRequired}
        isLoading={isUploading}
        btnText={btnText}
        className={className}
        onDrop={async (acceptedFiles, fileRejections) => {
          const filesToValidate =
            acceptedFiles.length > 0
              ? acceptedFiles
              : fileRejections.map((r) => r.file);

          field.onChange(filesToValidate);
          if (acceptedFiles.length > 0) {
            try {
              await onDrop(acceptedFiles, fileRejections).then((saved) =>
                setValue(name, [saved] as PathValue<T, Path<T>>),
              );
            } catch (e) {
              console.error('Upload failed', e);
            }
          }
        }}
        {...dropzoneOptions}
      />
      {(Array.isArray(field.value) ? field.value : []).map(
        ({ file }: { file: FileOutput }) => {
          if (!file) {
            return null;
          }
          return (
            <div className="flex items-center mt-2.5 gap-2.5 w-full rounded-5 px-5 py-2.5 bg-base-content border border-gray-opacity-15">
              <div className="flex gap-1.5 font-semibold justify-center items-center">
                <Doc className={'w-6 h-6 min-w-[24px] fill-med-gray-3'} />
                <div className="flex flex-col">
                  <div className="truncate w-full max-w-[220px]">
                    {file?.fileName}
                  </div>
                  <div className="text-med-gray-3 font-medium text-sm">
                    {file?.contentType} •{' '}
                    {formatFileSize(file?.size ?? 0) || '0 Kb'}
                  </div>
                </div>
              </div>

              <Close
                onClick={() => {
                  field.onChange([]);
                }}
                className="cursor-pointer fill-med-gray-3 ml-auto w-5 h-5 min-w-[20px]"
              />
            </div>
          );
        },
      )}
    </div>
  );
};

export const FormImagePreviewDropzone = <T extends FieldValues>({
  name,
  label,
  labelDropzone = 'Drop file here to upload',
  supportText = 'Supported file formats: JPG/JPEG, PNG, HEIC, HEIF',
  className,
  disabled,
  isRequired,
  labelClassName,
  onUpload,
  isUploading,
  ...dropzoneOptions
}: ControlledImageDropzoneProps<T>) => {
  const { control, clearErrors, setValue, watch } = useFormContext<T>();
  const id = `${name}Id` as Path<T>;
  const url = `${name}Url` as Path<T>;

  const preview = watch(url);

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: undefined,
  });

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: async (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        field.onChange(fileRejections.map(({ file }) => file));
        return;
      }
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        field.onChange(file);
        if (onUpload) {
          const { file: fileOutput } = await onUpload(file);
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setValue(id, fileOutput.fileId);
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setValue(url, fileOutput.url);
        }
        clearErrors(name);
      }
    },
    noClick: true,
    maxFiles: 1,
    ...dropzoneOptions,
  });

  const handleRemove = (e: React.MouseEvent) => {
    const id = `${name}Id` as Path<T>;
    e.stopPropagation();
    field.onChange(undefined);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setValue(id, null);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setValue(url, null);
  };

  if (isUploading) {
    return <Loader />;
  }

  return (
    <div
      className={twMerge(
        'flex w-full flex-col gap-0.5',
        disabled && 'opacity-50',
      )}
    >
      <div className="flex gap-1.5 items-center">
        {label && (
          <FieldLabel
            className={labelClassName}
            name={name}
            label={label}
            isRequired={isRequired}
          />
        )}
        {preview && (
          <ButtonComponent
            testID="remove"
            type="underline"
            htmlButtonType="button"
            className="ml-auto"
            Icon={() => (
              <TrashIcon className="fill-clc-blue w-5 h-5 min-w-[20px]" />
            )}
            iconPosition="left"
            onClick={handleRemove}
          >
            Delete
          </ButtonComponent>
        )}
      </div>

      <div
        className={twMerge(
          'relative w-full h-[280px] flex flex-col items-center justify-center border-2 border-dashed rounded-md overflow-hidden transition-colors',
          isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-400',
          error ? 'border-[#BF3F3F] bg-[#FFF4C6]' : 'bg-transparent',
          className,
        )}
        {...getRootProps()}
      >
        <input {...getInputProps()} id={name as string} />

        {preview ? (
          <div className="relative w-full h-full group">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="flex flex-col items-center p-6 text-center">
            <p className="text-lg font-semibold text-gray-800">
              {isDragActive ? 'Drop it here!' : labelDropzone}
            </p>
            <p className="text-sm text-gray-500 mt-1 mb-6">{supportText}</p>

            <ButtonComponent
              testID="choose"
              htmlButtonType="button"
              className="bg-[#3642e3] text-white text-sm font-bold tracking-wide py-2.5 px-6 rounded shadow-sm hover:bg-blue-700 transition-colors w-auto"
              onClick={open}
              disabled={disabled}
            >
              CHOOSE FILE
            </ButtonComponent>

            {error && (
              <div className="text-[#BF3F3F] text-xs font-semibold flex items-center gap-1 mt-4">
                <IconComponent
                  width={'w-3'}
                  height={'h-[11px]'}
                  icon={ErrorIcon}
                  fill={'fill-current'}
                  stroke={'stroke-current'}
                />
                <p>{error.message}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FormFileDropzone;
